import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../WidgetErrorMessage';
import CustomizableTable from './CustomizableTable';
import {
    allMetricSourcesAuthorized,
    hasMetricSources,
    getMetricSourcesForRequests,
    areGroupBysDifferent,
    areMetricSourcesTruncated,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../utils/wrapperUtils';

/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics }) {
    if (metricRequests.length === 0) {
        return { errorKey: 'invalidMetricRequests' };
    }

    if (areGroupBysDifferent(metricRequests)) {
        return { errorKey: 'metricRequestGroupBysDifferent' };
    }

    const metricSources = getMetricSourcesForRequests(metrics, metricRequests);
    if (metricSources.some((metricSource) => !metricSource)) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized(metricSources)) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources(metricSources, false)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        metricSources,
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSources, metricRequest: metricRequests[0] };
}
/* eslint-enable complexity */

function mergeDataSources(metricSources, groupBys) {
    if (!groupBys.length) {
        return metricSources[0].metricSeries;
    }

    const sourcesWithDataKeyedByGroups = metricSources.map((metricSource) =>
        _.keyBy(metricSource.metricSeries, (item) =>
            groupBys.map((groupBy) => `${item[groupBy]}`).join(';')
        )
    );

    return _({})
        .merge(...sourcesWithDataKeyedByGroups)
        .values()
        .value();
}

// eslint-disable-next-line complexity
export default function CustomizableTableWrapper(props) {
    const { metricSources, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const mergedDataSources = mergeDataSources(
        metricSources,
        metricRequest.groupBy
    );

    return (
        <>
            {areMetricSourcesTruncated(metricSources) && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${metricRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container">
                <CustomizableTable
                    {...props} // eslint-disable-line react/jsx-props-no-spreading
                    data={mergedDataSources}
                    formatValue={props.numberDecimalFormatter}
                    openLinksInNewTab={props.openLinksInNewTab}
                    metricRequest={metricRequest}
                    setDownloadData={props.setDownloadData}
                />
            </div>
        </>
    );
}

CustomizableTableWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    legendFormatter: PropTypes.func.isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    setDownloadData: PropTypes.func.isRequired,
};
